import { useEffect, useState } from 'react';

import { LOOKUP_SEARCH_DEBOUNCE_MS, LOOKUP_SEARCH_MIN_CHARS } from '../utils/tutorAiLimits';

/** Estado de um filtro de busca com debounce. */
export interface LookupSearchFilter {
  /** Texto já estabilizado, pronto para ir à API. */
  readonly debouncedFilter: string;
  /** Verdadeiro quando o texto tem tamanho suficiente e o debounce já passou. */
  readonly isReady: boolean;
  /** Verdadeiro enquanto o texto digitado ainda não estabilizou. */
  readonly isPending: boolean;
}

/**
 * Aplica debounce e mínimo de caracteres a um texto de busca de lookup.
 *
 * Extraído porque os filtros de curso e de turma precisam exatamente do mesmo
 * comportamento — duplicar o `useEffect` de debounce nos dois hooks convidaria os dois a
 * divergirem com o tempo.
 *
 * @param filter - Texto digitado pelo usuário
 * @returns Texto estabilizado e se a busca já pode disparar
 *
 * @example
 * ```ts
 * const { debouncedFilter, isReady } = useLookupSearchFilter(search);
 * ```
 */
export function useLookupSearchFilter(filter: string): LookupSearchFilter {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    // Limpar o campo deve zerar a busca na hora, sem esperar o debounce.
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }

    const timer = setTimeout(() => setDebouncedFilter(filter), LOOKUP_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter]);

  const isPending = filter !== debouncedFilter;

  return {
    debouncedFilter,
    isPending,
    isReady: !isPending && debouncedFilter.length >= LOOKUP_SEARCH_MIN_CHARS,
  };
}
